import { orderBy, where } from 'firebase/firestore'
import { useMemo } from 'react'
import { PortalConfig, SECTIONS, SECTION_IDS, computeProgress, resolveSectionCopy, type ClientDoc, type InviteDoc, type MemberDoc, type SectionDocs, type SectionId, type StaffDoc } from '@sonder/shared'
import { useCollection, useDoc } from './hooks'
import { applyOverlay, usePractice } from './practice'

export function useClient(clientId: string | null) {
  return useDoc<ClientDoc>(clientId ? `clients/${clientId}` : null)
}

export function usePortalConfig(clientId: string | null) {
  const state = useDoc<PortalConfig>(clientId ? `clients/${clientId}/config/portal` : null)
  const config = useMemo(() => {
    if (!state.data) return null
    const parsed = PortalConfig.safeParse(state.data)
    return parsed.success ? parsed.data : null
  }, [state.data])
  return { ...state, config }
}

/** Subscribes to all section docs at once; used by the rail (progress) and the review screen. */
export function useAllSections(clientId: string | null): { docs: Partial<SectionDocs>; loading: boolean } {
  const s = SECTION_IDS.map((id) => useDoc(clientId ? `clients/${clientId}/sections/${id}` : null)) // eslint-disable-line react-hooks/rules-of-hooks
  // In practice mode, progress and the review show the practice edits too.
  const practice = usePractice()
  const docs = useMemo(() => {
    const out: Partial<SectionDocs> = {}
    SECTION_IDS.forEach((id, i) => {
      const data = s[i]!.data
      if (data) (out as Record<SectionId, unknown>)[id] = practice ? applyOverlay(data, practice.overlay(`clients/${clientId}/sections/${id}`)) : data
    })
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...s.map((x) => x.data), practice, clientId])
  return { docs, loading: s.some((x) => x.loading) }
}

export function useProgress(config: PortalConfig | null, docs: Partial<SectionDocs>) {
  return useMemo(() => (config ? computeProgress(config, docs) : null), [config, docs])
}

export function useMembers(clientId: string | null) {
  return useCollection<MemberDoc>(clientId ? `clients/${clientId}/members` : null)
}

/**
 * Invitations to a client that have not been accepted: pending, or pending-but-expired (still shown so
 * staff can resend). Staff only; the invites collection is not readable by client members.
 */
export function useClientInvites(clientId: string | null) {
  const state = useCollection<InviteDoc>(clientId ? 'invites' : null, [where('clientId', '==', clientId)], [clientId])
  const items = useMemo(() => state.items.filter((i) => i.status === 'pending').sort((a, b) => b.createdAt - a.createdAt), [state.items])
  return { ...state, items }
}

/** `enabled: false` skips the read (the account menu only needs it for team members). */
export function useStaffDirectory(enabled = true) {
  return useCollection<StaffDoc>(enabled ? 'staff' : null, [orderBy('order')])
}

/**
 * Enabled sections in configured order, with registry metadata. `number` is the step's position in
 * this client's order ("01", "02", …), not the catalogue's default, so reordering renumbers everywhere.
 * Pass `brandCount` wherever the client sees the wording: a single-brand client gets singular copy.
 */
export function enabledSections(config: PortalConfig | null, brandCount?: number) {
  if (!config) return []
  return config.sections
    .filter((s) => s.enabled)
    .map((s, i) => ({ ...(brandCount === undefined ? SECTIONS[s.id] : resolveSectionCopy(SECTIONS[s.id], brandCount)), number: stepNumber(i) }))
}

/** How many brands this client has; undefined until the brands doc has loaded. */
export function useBrandCount(clientId: string | null): number | undefined {
  const { data, loading } = useDoc<{ order?: string[] }>(clientId ? `clients/${clientId}/sections/brands` : null)
  if (loading) return undefined
  return data?.order?.length ?? 0
}

export function stepNumber(index: number): string {
  return String(index + 1).padStart(2, '0')
}
